import { GraphQLVehicleGroupsRequester } from '@cobliteam/api-lib';
import { useQuery } from '@tanstack/react-query';

// PRD R1 (mirrors modules/groups/src/pages/GroupsPage.tsx's isGroupCountEligible): fleets with 2+
// groups are ineligible for the group AI suggestion fallback.
export const GROUP_COUNT_ELIGIBILITY_MAX = 1;
export const isGroupCountEligible = (groupCount: number): boolean => groupCount <= GROUP_COUNT_ELIGIBILITY_MAX;

export const groupCountQueryKeys = {
  all: () => ['AI_RECOMMENDATIONS_GROUP_COUNT'] as const,
  byFleet: (fleetId: string | undefined) => [...groupCountQueryKeys.all(), fleetId] as const,
};

const STALE_TIME_MS = 10 * 60 * 1000;

// Lazy instantiation (same pattern as useEntity/useVehicleGroup) so importing this module never
// touches @cobliteam/api-lib's constructor eagerly — only when the query actually runs.
const getVehicleGroupsRequester = () => new GraphQLVehicleGroupsRequester();

/**
 * AE-2569: cheap fleet-wide group-count check backing the "flag OR under-threshold" fallback for
 * group AI suggestions in the floating card (a fleet without `aiGroupRecommendations`/
 * `aiRecommendations2` should still see group suggestions while it has 0-1 groups).
 *
 * Only fetches when `enabled` — callers that already have flag-based access should pass `false` to
 * skip the extra request.
 */
const useIsGroupCountEligible = (fleetId: string | undefined, enabled = true) => {
  const { data, isLoading } = useQuery({
    queryKey: groupCountQueryKeys.byFleet(fleetId),
    queryFn: () => getVehicleGroupsRequester().getVehicleGroupsCount(),
    enabled: !!fleetId && enabled,
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // `hasCount` separa "0 grupos" de "contagem indisponível" (carregando ou erro), em que `data` é `undefined`.
  return { isEligible: isGroupCountEligible(data ?? 0), isLoading, hasCount: data !== undefined, groupCount: data ?? 0 };
};

export default useIsGroupCountEligible;
